//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from '@/redux/hooks';
import { FilterIDType, FilterKeyMap } from '@/components/_common/Filters/FilterID';
import { applyFilter, clearFilters } from '@/redux/slices/dataTableSlice';
import { NotificationRecord } from '@/redux/slices/notificationSlice';

export const useNotificationNavigation = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  // Helper function to apply filters and navigate with query params
  const navigateWithFilters = useCallback((
    path: string, 
    filters: Record<string, any>, 
    additionalParams?: Record<string, string>
  ) => {
    const isAlreadyOnPath = history.location.pathname === path;
    
    if (isAlreadyOnPath) {
      dispatch(clearFilters(undefined));
      dispatch(applyFilter({ type: 'filter_id', selections: filters.filter_id }));
    }

    const params = new URLSearchParams();
    params.set('filters', JSON.stringify(filters));
    
    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        params.set(key, value);
      });
    }

    history.push(`${path}?${params.toString()}`, { replace: true });
  }, [dispatch, history]);

  // Helper function to apply asset filter and navigate to pending assets
  const navigateToPendingAssets = useCallback((assetId: string | number) => {
    const filters = {
      filter_id: [`A${assetId}`, FilterKeyMap[FilterIDType.ASSET_ID]]
    };
    navigateWithFilters('/pending-assets', filters);
  }, [navigateWithFilters]);

  // Helper function to navigate to job details
  const navigateToJob = useCallback((jobId: string | number) => {
    history.push(`/jobs/${jobId}`);
  }, [history]);

  // Helper function to navigate to exceptions with filter
  const navigateToExceptions = useCallback((exceptionId: string | number) => {
    const filters = {
      filter_id: [`X${exceptionId}`, FilterKeyMap[FilterIDType.EXCEPTION_ID]]
    };
    navigateWithFilters('/exceptions', filters);
  }, [navigateWithFilters]);

  // Helper function to navigate to tasks with filter
  const navigateToTasks = useCallback((taskId: string | number) => {
    const filters = {
      filter_id: [`T${taskId}`, FilterKeyMap[FilterIDType.TASK_ID]]
    };
    navigateWithFilters('/tasks', filters, { id: taskId.toString() });
  }, [navigateWithFilters]);

  // Main navigation handler for notifications
  const handleNotificationNavigation = useCallback((notif: NotificationRecord) => {
    try {
      if (!notif.notification.type) {
        console.warn('Notification has no type:', notif.id);
        return;
      }

      const { data, type } = notif.notification;

      // Validate data exists
      if (!data) {
        console.warn('Notification has no data:', notif.id);
        return;
      }

      // Route based on notification data priority
      if (data.job_asset_id) {
        navigateToPendingAssets(data.job_asset_id);
      } else if (type.toLowerCase().includes('job') && data.job_id) {
        navigateToJob(data.job_id);
      } else if (data.processing_exception_id) {
        navigateToExceptions(data.processing_exception_id);
      } else if (data.task_id) {
        navigateToTasks(data.task_id);
      } else {
        console.warn('Unable to determine navigation route for notification:', notif.id, { type, data });
      }
    } catch (error) {
      console.error('Error handling notification navigation:', error, notif);
    }
  }, [navigateToPendingAssets, navigateToJob, navigateToExceptions, navigateToTasks]);

  return {
    handleNotificationNavigation,
    navigateToPendingAssets,
    navigateToJob,
    navigateToExceptions,
    navigateToTasks,
    navigateWithFilters
  };
};
